import React from "react";
import { FormControlLabel, RadioGroup, Typography } from "@mui/material";
import { FormField, Radio } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { Stack } from "components/layout/reusableLayouts";

interface ApprovalRoleChecksProps {
  approvalRole: string;
  onChangeApprovalRole: (value: string) => void;
  error?: string;
}

// Each value is the contract with getApprovalRoles in useSettingPolicyForm,
// which switches on this exact spelling to build the Role[] sent to the API.
const APPROVAL_ROLES = [
  {
    value: "noApprovalsRequired",
    title: "No Approvals Required",
    description: "noApprovalsRequiredDescription",
  },
  {
    value: "managerApprovalOnly",
    title: "Manager approval",
    description: "managerApprovalOnlyDescription",
  },
  {
    value: "administratorApprovalOnly",
    title: "Administrator approval",
    description: "administratorApprovalOnlyDescription",
  },
  {
    value: "managerAndAccountant",
    title: "Manager + Accountant",
    description: "managerAndAccountantDescription",
  },
  {
    value: "managerAndAdministrators",
    title: "Manager + Administrator",
    description: "managerAndAdministratorsDescription",
  },
];

// A static id, not useId: React's colons break the CSS selectors RTL builds.
const LABEL_ID = "approval-roles-label";

const ApprovalRoleChecks: React.FC<ApprovalRoleChecksProps> = ({
  approvalRole,
  onChangeApprovalRole,
  error,
}) => {
  const { t } = useTranslation();

  return (
    <Stack gap="var(--space-4)">
      {/* A field label, matching the User Group select beside it, rather than a
          heading: it names five radios, so it is tied to them by id. */}
      <Typography
        id={LABEL_ID}
        variant="label"
        color="var(--color-text-secondary)"
      >
        {t("Approval Roles")}
      </Typography>
      <FormField errorText={error}>
        <RadioGroup
          aria-labelledby={LABEL_ID}
          name="workflowSettings"
          value={approvalRole}
          onChange={(event) => onChangeApprovalRole(event.target.value)}
          sx={{ gap: "var(--space-7)" }}
        >
          {APPROVAL_ROLES.map((role) => (
            <FormControlLabel
              key={role.value}
              value={role.value}
              sx={{ alignItems: "flex-start" }}
              control={<Radio />}
              label={
                <Stack gap="var(--space-1)">
                  <Typography variant="subtitle2">{t(role.title)}</Typography>
                  <Typography variant="body2">{t(role.description)}</Typography>
                </Stack>
              }
            />
          ))}
        </RadioGroup>
      </FormField>

      <Typography variant="body2">
        {t("Admins can always approve in the place of a manager")}
      </Typography>
    </Stack>
  );
};

export default ApprovalRoleChecks;
